import { extend } from "@dhx/ts-common/core";
import { create, el } from "@dhx/ts-common/dom";
import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { IHandlers } from "@dhx/ts-common/types";
import { View, toViewLike, IView } from "@dhx/ts-common/view";
import { Layout } from "@dhx/ts-layout";
import { Slider, SliderEvents } from "@dhx/ts-slider";
import locale from "./locales/en";
import { isTimeCheck } from "./helper";
import {
	TimepickerEvents,
	ITimepickerConfig,
	ITimepicker,
	ITimeObject,
	ITimepickerHandlersMap,
} from "./types";
import { isFirefox, isSafari } from "@dhx/ts-common/html";

function validate(value: number, max: number): number {
	if (isNaN(value)) {
		return 0;
	}
	return Math.min(max, Math.max(0, value));
}

export class Timepicker extends View implements ITimepicker {
	public config: ITimepickerConfig;
	public events: IEventSystem<TimepickerEvents, ITimepickerHandlersMap>;
	public layout: Layout;
	private _hoursSlider: Slider;
	private _minutesSlider: Slider;
	private _inputsView: IView;
	private _time: ITimeObject;
	private _handlers: IHandlers;
	private _outerHandlers;

	constructor(container: HTMLElement | string, config: ITimepickerConfig = {}) {
		super(
			container,
			extend(
				{
					timeFormat: 24,
					controls: false,
					valueFormat: "string",
					actions: false, // TODO: remove suite_7.0
				},
				config
			)
		);
		this.events = new EventSystem(this);
		this._time = {
			hour: 0,
			minute: 0,
			AM: true,
		};
		if (this.config.timeFormat === 12) {
			this._time.hour = 12;
		}
		this.config.controls = this.config.controls || this.config.actions; // TODO: remove suite_7.0
		this.config.value && this._setValue(this.config.value);

		this._initUI(container);
		this._initHandlers();
		this._initEvents();
	}
	getValue<T extends boolean = false>(asOBject?: T): T extends true ? ITimeObject : string {
		if (this.config.timeFormat === 12) this._time.hour = this._time.hour % 12 || 12;
		const { hour: h, minute: m, AM: isAM } = this._time;
		if (asOBject) {
			const obj: { hour: number; minute: number; AM?: boolean } = {
				hour: h,
				minute: m,
			};
			if (this.config.timeFormat === 12) {
				obj.AM = isAM;
			}
			return obj as any;
		}
		return ((h < 10 ? "0" + h : h) +
			":" +
			(m < 10 ? "0" + m : m) +
			(this.config.timeFormat === 12 ? (isAM ? "AM" : "PM") : "")) as any;
	}
	setValue(value: Date | number | string | any[] | ITimeObject): void {
		this._setValue(value);
		this._hoursSlider.setValue(this._time.hour);
		this._minutesSlider.setValue(this._time.minute);

		this._inputsView.paint();
	}
	clear(): void {
		if (this.config.timeFormat === 24) {
			this.setValue("00:00");
		} else {
			this.setValue("12:00AM");
		}
	}
	destructor(): void {
		this._minutesSlider && this._minutesSlider.destructor();
		this._hoursSlider && this._hoursSlider.destructor();
		this.events && this.events.clear();
		this.layout && this.layout.destructor();

		this.config = this.events = null;
		this._handlers = this._time = this._inputsView = this._minutesSlider = this._hoursSlider = null;
		this.unmount();
	}
	getRootView() {
		return this.layout.getRootView();
	}
	private _setValue(value: Date | number | string | any[] | ITimeObject): ITimeObject {
		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		let m = 0;
		let h = 0;
		let isPM: boolean;
		if (typeof value === "number") {
			value = new Date(value);
		}
		if (value instanceof Date) {
			m = value.getMinutes();
			h = value.getHours();
		} else if (Array.isArray(value)) {
			h = validate(value[0], 23);
			m = validate(value[1], 59);
			if (value[2] && value[2].toLowerCase() === "pm") {
				isPM = true;
			}
		} else if (typeof value === "string") {
			const matches = value.match(/\d+/g);
			h = validate(+matches[0], 23);
			m = validate(+matches[1], 59);
			if (value.toLowerCase().includes("pm")) {
				isPM = true;
			}
		} else if (
			typeof value === "object" &&
			value.hasOwnProperty("hour") &&
			value.hasOwnProperty("minute")
		) {
			h = value.hour;
			m = value.minute;
			isPM = !value.AM;
		}
		if (isPM && h < 12) {
			h += 12;
		}
		if (this.config.timeFormat === 12 && !isTimeCheck(value) && h >= 12) isPM = true;

		return (this._time = {
			hour: h,
			minute: m,
			AM: !isPM,
		});
	}
	private _initUI(container) {
		const layoutConfig = {
			gravity: false,
			css:
				"dhx_widget dhx_timepicker " +
				(this.config.css ? this.config.css : "") +
				(this.config.controls ? " dhx_timepicker--with-controls" : ""),
			rows: [
				{
					id: "timepicker",
					css: "dhx_timepicker__inputs",
				},
				{
					id: "hour-slider",
					css: "dhx_timepicker__hour",
				},
				{
					id: "minute-slider",
					css: "dhx_timepicker__minute",
				},
			],
		};
		if (this.config.controls) {
			layoutConfig.rows.unshift({
				id: "close-action",
				css: "dhx_timepicker__close",
			});
			layoutConfig.rows.push({
				id: "save-action",
				css: "dhx_timepicker__save",
			});
		}
		const layout = (this.layout = new Layout(container, layoutConfig));

		const timepicker = create({
			render: () => this._draw(),
		});
		const inputsView = (this._inputsView = toViewLike(timepicker));

		const mSlider = (this._minutesSlider = new Slider(null, {
			min: 0,
			max: 59,
			step: 1,
			tooltip: false,
			labelPosition: "top",
			label: locale.minutes,
			value: this.config.value ? this._time.minute : 0,
		}));

		const hSlider = (this._hoursSlider = new Slider(null, {
			min: 0,
			max: 23,
			step: 1,
			tooltip: false,
			labelPosition: "top",
			label: locale.hours,
			value: this.config.value ? (this._time.hour === 12 && this._time.AM ? 0 : this._time.hour) : 0,
		}));

		layout.getCell("timepicker").attach(inputsView);
		layout.getCell("hour-slider").attach(hSlider);
		layout.getCell("minute-slider").attach(mSlider);

		if (this.config.controls) {
			const save = () => {
				return el(
					"button.dhx_timepicker__button-save.dhx_button.dhx_button--view_flat.dhx_button--color_primary.dhx_button--size_small.dhx_button--circle.dhx_button--width_full",
					{
						onclick: this._outerHandlers.save,
						type: "button",
					},
					locale.save
				);
			};
			const close = () => {
				return el(
					"button.dhx_timepicker__button-close.dhx_button.dhx_button--view_link.dhx_button--size_medium.dhx_button--view_link.dhx_button--color_secondary.dhx_button--icon.dhx_button--circle",
					{
						_ref: "close",
						onclick: this._outerHandlers.close,
						type: "button",
						"aria-label": "close timepicker",
					},
					[el("span.dhx_button__icon.dxi.dxi-close")]
				);
			};
			layout.getCell("save-action").attach(save);
			layout.getCell("close-action").attach(close);
		}
	}
	private _initHandlers() {
		const setMinutes = (element: HTMLInputElement) => {
			const min = validate(parseInt(element.value, 10), 59);
			element.value = min.toString();
			this._minutesSlider.setValue(min);
		};
		const setHours = (element: HTMLInputElement) => {
			const hour = validate(parseInt(element.value, 10), 23);
			element.value = hour.toString();
			this._hoursSlider.setValue(hour);
		};
		this._handlers = {
			onchange: {
				".dhx_timepicker-input--hour": e => setHours(e.target),
				".dhx_timepicker-input--minutes": e => setMinutes(e.target),
			},
			oninput: {
				".dhx_timepicker-input--hour": e => {
					if (!isSafari() && !isFirefox()) return;
					setHours(e.target);
				},
				".dhx_timepicker-input--minutes": e => {
					if (!isSafari() && !isFirefox()) return;
					setMinutes(e.target);
				},
			},
		};
		this._outerHandlers = {
			close: () => {
				if (
					!this.events.fire(TimepickerEvents.beforeClose, [
						this.getValue(this.config.valueFormat === "timeObject"),
					])
				) {
					return;
				}
				this.events.fire(TimepickerEvents.afterClose, [
					this.getValue(this.config.valueFormat === "timeObject"),
				]);
				this.events.fire(TimepickerEvents.close, []); // TODO: remove suite_7.0
			},
			save: () => {
				if (
					!this.events.fire(TimepickerEvents.beforeApply, [
						this.getValue(this.config.valueFormat === "timeObject"),
					])
				)
					return;
				this.events.fire(TimepickerEvents.afterApply, [
					this.getValue(this.config.valueFormat === "timeObject"),
				]);
				this.events.fire(TimepickerEvents.apply, [this.getValue()]); // TODO: remove suite_7.0
				this.events.fire(TimepickerEvents.save, [this._time]); // TODO: remove suite_7.0
			},
		};
	}
	private _initEvents() {
		this._hoursSlider.events.on(SliderEvents.change, value => {
			if (value < this._hoursSlider.config.min || value > this._hoursSlider.config.max) {
				return;
			}
			if (this.config.timeFormat === 12) {
				this._time.AM = value < 12;
				this._time.hour = value % 12 || 12;
			} else {
				this._time.hour = value;
			}
			this.events.fire(TimepickerEvents.change, [
				this.getValue(this.config.valueFormat === "timeObject"),
			]);
			this._inputsView.paint();
		});
		this._minutesSlider.events.on(SliderEvents.change, value => {
			if (value < this._minutesSlider.config.min || value > this._minutesSlider.config.max) {
				return;
			}
			this._time.minute = value;
			this.events.fire(TimepickerEvents.change, [
				this.getValue(this.config.valueFormat === "timeObject"),
			]);
			this._inputsView.paint();
		});
	}
	private _draw() {
		return el(
			".dhx_timepicker-inputs",
			{
				...this._handlers,
			},
			[
				el("input.dhx_timepicker-input.dhx_timepicker-input--hour", {
					_key: "hour",
					_ref: "hour",
					value:
						this.getValue(true).hour.toString().length > 1
							? this.getValue(true).hour
							: "0" + this.getValue(true).hour,
					"aria-label": "hours",
				}),
				el("span.dhx_timepicker-delimer", ":"),
				el("input.dhx_timepicker-input.dhx_timepicker-input--minutes", {
					_key: "minute",
					value:
						this.getValue(true).minute.toString().length > 1
							? this.getValue(true).minute
							: "0" + this.getValue(true).minute,
					"aria-label": "minutes",
				}),
				this.config.timeFormat === 12
					? el(".dhx_timepicker-ampm", this._time.AM ? "AM" : "PM")
					: null,
			]
		);
	}
}
